/**
 * Listing rules shared by the app, the dealer portal / admin panel and Cloud
 * Functions: one implementation so the three can never drift apart again.
 *
 * Plain CommonJS with no dependencies so every runtime can load it (Metro,
 * Jest, CRA webpack and Node). The canonical copy lives in src/domain next to
 * propertyTaxonomy.json; `npm run sync:taxonomy` copies both into
 * admin/src/shared and admin/functions/shared, and a test fails if the copies
 * differ. Types for the app live in listingRules.d.ts.
 *
 * Avoid array destructuring and classes here: the admin app's Babel compiles
 * them to helper *imports*, which turns this file into an ES module and drops
 * module.exports from the admin build.
 */

const TAXONOMY = require('./propertyTaxonomy.json');

const LISTING_TYPE_IDS = TAXONOMY.listingTypes.map((l) => l.id);
const CATEGORY_IDS = TAXONOMY.propertyCategories.map((c) => c.id);
const PROPERTY_TYPE_IDS = Array.from(new Set(TAXONOMY.propertyTypes.map((t) => t.id)));
const HIDDEN_STATUSES = ['deleted', 'inactive', 'pending'];

// Fields every listing carries (old clients read them unconditionally), so
// they are always written, with an empty/zero value when they don't apply.
const CORE_FIELDS = [
  'title', 'description', 'price', 'priceUnit', 'isNegotiable',
  'address', 'locality', 'city', 'pincode', 'societyName', 'googleMapsUrl',
  'category', 'propertyCategory', 'propertyType',
  'rooms', 'bathrooms', 'area', 'areaValue', 'areaUnit',
  'readyToMove', 'possessionDate', 'availableFrom',
  'facilities', 'officeId',
];

// Every optional, type-dependent field. Anything here that doesn't apply to
// the listing (or is left empty) is reported in `removedKeys`, so an edit that
// changes the type also clears the old type's leftovers.
const OPTIONAL_FIELDS = Array.from(new Set([
  ...Object.values(TAXONOMY.fieldsByKind).flat(),
  ...Object.values(TAXONOMY.fieldsByUse).flat(),
  ...Object.values(TAXONOMY.fieldsByListingType).flat(),
])).filter((f) => !CORE_FIELDS.includes(f));

const NUMERIC_FIELDS = [
  'floorNumber', 'totalFloors', 'coveredParking', 'openParking', 'balconies',
  'plotLength', 'plotBreadth', 'roadWidthFt',
  'seats', 'cabins', 'meetingRooms', 'frontageFt', 'clearHeightFt', 'loadingDocks', 'powerLoadKva',
  'securityDeposit', 'maintenance', 'lockInMonths', 'noticePeriodDays', 'escalationPercent', 'currentRent',
];
const BOOLEAN_FIELDS = [
  'cornerPlot', 'boundaryWall', 'pantry', 'mainRoadFacing', 'maintenanceIncluded',
  'petsAllowed', 'nonVegAllowed', 'preLeased',
];
const ARRAY_FIELDS = ['pgSuitableFor', 'meals', 'pgServices', 'preferredTenants'];
const WEEK_DAYS = TAXONOMY.options.weekDays.map((d) => d.id);
const MEAL_IDS = TAXONOMY.options.meals.map((m) => m.id);

// ─── Small helpers ──────────────────────────────────────────────────────────

function isPresent(v) {
  if (v === undefined || v === null) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

/** Parses "25,000", " 1.5 " or 25000 into a finite number; anything else → null. */
function toNumber(v) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v !== 'string') return null;
  const cleaned = v.replace(/[₹,\s]/g, '');
  if (cleaned === '' || !/^-?\d*\.?\d+$/.test(cleaned)) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/** Indian digit grouping: 8500000 → "85,00,000". */
function formatIndianNumber(value) {
  const n = toNumber(value);
  if (n === null) return '';
  const negative = n < 0;
  const parts = String(Math.abs(n)).split('.');
  const intPart = parts[0];
  const decPart = parts[1];
  const lastThree = intPart.slice(-3);
  const rest = intPart.slice(0, -3);
  const grouped = rest
    ? `${rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${lastThree}`
    : lastThree;
  return `${negative ? '-' : ''}${grouped}${decPart ? `.${decPart}` : ''}`;
}

/** ₹ shorthand used in titles and summaries: 4500000 → "₹45 L", 25000 → "₹25K". */
function formatPriceShort(value) {
  const n = toNumber(value);
  if (n === null) return '';
  const trim = (x) => String(Math.round(x * 100) / 100);
  if (n >= 10000000) return `₹${trim(n / 10000000)} Cr`;
  if (n >= 100000) return `₹${trim(n / 100000)} L`;
  if (n >= 1000) return `₹${trim(n / 1000)}K`;
  return `₹${n}`;
}

function titleCase(s) {
  return String(s || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

// ─── Normalisation ──────────────────────────────────────────────────────────

function normalizeCategory(value) {
  if (typeof value !== 'string') return null;
  const v = value.trim().toLowerCase();
  return CATEGORY_IDS.includes(v) ? v : null;
}

/** Canonical type id ('apartment' → 'flat'); null for empty input. */
function normalizePropertyType(value) {
  if (typeof value !== 'string') return null;
  const v = value.trim().toLowerCase();
  if (!v) return null;
  return TAXONOMY.propertyTypeAliases[v] || v;
}

/**
 * Canonical transaction for a listing: 'sell' → 'buy', and commercial rentals
 * are always 'lease' (residential ones always 'rent'). The app queries
 * (propertyCategory, category) pairs, so a commercial 'rent' would never show.
 */
function normalizeListingType(value, propertyCategory) {
  if (typeof value !== 'string') return null;
  let v = value.trim().toLowerCase();
  v = TAXONOMY.listingTypeAliases[v] || v;
  if (!LISTING_TYPE_IDS.includes(v)) return null;
  const category = normalizeCategory(propertyCategory);
  if (category === 'commercial' && v === 'rent') return 'lease';
  if (category === 'residential' && v === 'lease') return 'rent';
  return v;
}

function getPropertyTypeDef(propertyCategory, propertyType) {
  const type = normalizePropertyType(propertyType);
  if (!type) return null;
  const category = normalizeCategory(propertyCategory);
  return (
    TAXONOMY.propertyTypes.find((t) => t.id === type && (!category || t.category === category)) ||
    TAXONOMY.propertyTypes.find((t) => t.id === type) ||
    null
  );
}

/** 'home' | 'pg' | 'plot' | 'commercial': decides which fields apply. */
function getKind(propertyCategory, propertyType) {
  const def = getPropertyTypeDef(propertyCategory, propertyType);
  if (def) return def.kind;
  return normalizeCategory(propertyCategory) === 'commercial' ? 'commercial' : 'home';
}

function getPropertyTypes(propertyCategory, listingType) {
  const category = normalizeCategory(propertyCategory);
  const listing = listingType ? normalizeListingType(listingType, category) : null;
  return TAXONOMY.propertyTypes.filter(
    (t) => (!category || t.category === category) && (!listing || t.listingTypes.includes(listing)),
  );
}

function getListingTypesFor(propertyCategory, propertyType) {
  const def = getPropertyTypeDef(propertyCategory, propertyType);
  return def ? def.listingTypes.slice() : [];
}

function isValidCombination(propertyCategory, propertyType, listingType) {
  const def = getPropertyTypeDef(propertyCategory, propertyType);
  if (!def || def.category !== normalizeCategory(propertyCategory)) return false;
  const listing = normalizeListingType(listingType, propertyCategory);
  return !!listing && def.listingTypes.includes(listing);
}

// ─── Labels ─────────────────────────────────────────────────────────────────

function getTypeLabel(propertyType, propertyCategory, short) {
  const def = getPropertyTypeDef(propertyCategory, propertyType);
  if (def) return short ? def.shortLabel : def.label;
  return titleCase(propertyType);
}

function getListingTypeDef(listingType, propertyCategory) {
  const id = normalizeListingType(listingType, propertyCategory);
  return TAXONOMY.listingTypes.find((l) => l.id === id) || null;
}

/** "For Sale" / "For Rent" / "For Lease". */
function getListingBadge(listingType, propertyCategory) {
  const def = getListingTypeDef(listingType, propertyCategory);
  return def ? def.badge : '';
}

/**
 * Label for an option id or value. `group` is a key of TAXONOMY.options;
 * furnishing is looked up across its home and commercial lists.
 */
function getOptionLabel(group, id) {
  const raw = TAXONOMY.options[group];
  if (!raw || id === undefined || id === null || id === '') return '';
  const list = Array.isArray(raw) ? raw : Object.values(raw).flat();
  const hit = list.find((o) => o.id === id || o.value === id);
  return hit ? hit.label : titleCase(id);
}

function getFurnishingOptions(kind) {
  return kind === 'commercial' ? TAXONOMY.options.furnishing.commercial : TAXONOMY.options.furnishing.home;
}

function getPriceUnit(listingType, kind) {
  if (kind === 'pg') return TAXONOMY.pgPriceUnit;
  const def = TAXONOMY.listingTypes.find((l) => l.id === listingType);
  return def ? def.priceUnit : '';
}

function getPriceBrackets(intent) {
  return TAXONOMY.priceBrackets[intent] || TAXONOMY.priceBrackets.buy;
}

// ─── Field applicability ────────────────────────────────────────────────────

/** Set of optional field keys that apply to this category / type / transaction. */
function getApplicableFields(propertyCategory, propertyType, listingType) {
  const def = getPropertyTypeDef(propertyCategory, propertyType);
  const kind = getKind(propertyCategory, propertyType);
  const listing = normalizeListingType(listingType, propertyCategory);
  const fields = new Set(TAXONOMY.fieldsByKind[kind] || []);

  if (def && def.use) (TAXONOMY.fieldsByUse[def.use] || []).forEach((f) => fields.add(f));
  if (def && def.standalone) fields.delete('floorNumber');

  if (listing === 'buy') {
    ['possessionDate', 'ownership', 'reraId'].forEach((f) => fields.add(f));
    if (kind !== 'plot') ['ageOfProperty', 'saleType'].forEach((f) => fields.add(f));
    if (kind === 'commercial') ['preLeased', 'currentRent'].forEach((f) => fields.add(f));
  } else if (kind === 'pg') {
    ['availableFrom', 'securityDeposit', 'noticePeriodDays'].forEach((f) => fields.add(f));
  } else if (listing === 'rent') {
    TAXONOMY.fieldsByListingType.rent
      .filter((f) => f !== 'noticePeriodDays')
      .forEach((f) => fields.add(f));
  } else if (listing === 'lease') {
    TAXONOMY.fieldsByListingType.lease.forEach((f) => fields.add(f));
  }
  return fields;
}

function hasBhk(propertyCategory, propertyType) {
  const def = getPropertyTypeDef(propertyCategory, propertyType);
  return !!def && def.kind === 'home' && !def.noBhk;
}

// ─── Area ───────────────────────────────────────────────────────────────────

function getAreaUnit(unitId) {
  return TAXONOMY.options.areaUnits.find((u) => u.id === unitId) || TAXONOMY.options.areaUnits[0];
}

/** Value in the given unit → whole sq ft (how `area` is stored and filtered). */
function toSqft(value, unitId) {
  const n = toNumber(value);
  if (n === null || n <= 0) return 0;
  return Math.round(n * getAreaUnit(unitId).toSqft);
}

/** "1,200 sq ft", in the unit the lister used when they picked one. */
function formatArea(listing) {
  if (!listing) return '';
  const unit = listing.areaUnit && listing.areaUnit !== 'sqft' ? getAreaUnit(listing.areaUnit) : null;
  const value = unit ? toNumber(listing.areaValue) : toNumber(listing.area);
  if (!value || value <= 0) return '';
  return `${formatIndianNumber(value)} ${unit ? unit.label : 'sq ft'}`;
}

// ─── Display ────────────────────────────────────────────────────────────────

/**
 * Short configuration label for cards: "2 BHK", "5+ BHK", "Studio / 1 RK",
 * "Boys PG", "Office Space". Plots return '' (their area is the headline).
 */
function getConfigLabel(listing) {
  if (!listing) return '';
  const def = getPropertyTypeDef(listing.propertyCategory, listing.propertyType);
  const kind = getKind(listing.propertyCategory, listing.propertyType);
  if (kind === 'plot') return '';
  if (kind === 'pg') {
    return listing.pgFor ? `${getOptionLabel('pgFor', listing.pgFor).replace(' (anyone)', '')} PG` : 'PG';
  }
  if (kind === 'commercial') return def ? def.label : titleCase(listing.propertyType);
  if (def && def.noBhk) return def.label;
  const rooms = toNumber(listing.rooms);
  if (!rooms || rooms <= 0) return '';
  return rooms >= 5 ? '5+ BHK' : `${rooms} BHK`;
}

function isBrowseVisible(listing) {
  return !!listing && !HIDDEN_STATUSES.includes(listing.status);
}

function todayIso(now) {
  const d = now ? new Date(now) : new Date();
  const pad = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Rentals: available now when no date, 'Immediately', or a date that has passed. */
function isAvailableNow(availableFrom, now) {
  if (!availableFrom || String(availableFrom).toLowerCase().startsWith('immediate')) return true;
  const iso = String(availableFrom).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return true;
  return iso <= todayIso(now);
}

// ─── PG meal schedule ───────────────────────────────────────────────────────

/**
 * A per-day meal plan ({mon: ['breakfast', 'dinner'], …}), or null when every
 * day is the same, in which case `meals` alone describes it.
 */
function normalizeMealSchedule(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const schedule = {};
  for (const day of WEEK_DAYS) {
    const list = Array.isArray(raw[day]) ? raw[day] : [];
    schedule[day] = MEAL_IDS.filter((m) => list.includes(m));
  }
  const first = schedule[WEEK_DAYS[0]].join(',');
  if (WEEK_DAYS.every((d) => schedule[d].join(',') === first)) return null;
  return schedule;
}

/** "Mon–Fri: breakfast and dinner · Sat–Sun: breakfast, lunch and dinner". */
function describeMealSchedule(schedule) {
  if (!schedule) return '';
  const label = (d) => getOptionLabel('weekDays', d);
  const runs = [];
  WEEK_DAYS.forEach((day) => {
    const key = (schedule[day] || []).join(',');
    const last = runs[runs.length - 1];
    if (last && last.key === key) last.to = day;
    else runs.push({ key, from: day, to: day, meals: schedule[day] || [] });
  });
  return runs.map((r) => {
    const days = r.from === r.to ? label(r.from) : `${label(r.from)}–${label(r.to)}`;
    const meals = r.meals.length ? joinWithAnd(r.meals.map((m) => getOptionLabel('meals', m).toLowerCase())) : 'no meals';
    return `${days}: ${meals}`;
  }).join(' · ');
}

// ─── Validation & suggestions ───────────────────────────────────────────────

const TITLE_FORBIDDEN = /[<>{}[\]\\|^~`]/;
const TITLE_FORBIDDEN_ALL = /[<>{}[\]\\|^~`]/g;
// A letter in any script: an ASCII letter or any non-ASCII character
// (Devanagari etc.). Avoids \p{L}, which not every JS engine supports.
const TITLE_HAS_LETTER = /[A-Za-z]|[\u0080-￿]/;

/** null when fine, otherwise a message. Allows punctuation and Indian scripts. */
function validateTitle(title) {
  const t = String(title || '').trim();
  const { titleMin, titleMax } = TAXONOMY.limits;
  if (t.length < titleMin) return `Title must be at least ${titleMin} characters.`;
  if (t.length > titleMax) return `Title must be under ${titleMax} characters.`;
  if (TITLE_FORBIDDEN.test(t)) return 'Title can’t contain < > { } [ ] \\ | ^ ~ or `.';
  if (!TITLE_HAS_LETTER.test(t)) return 'Title needs some words, not just numbers.';
  return null;
}

function getPgSharingRents(values) {
  const sharing = values && values.pgSharing;
  const list = Array.isArray(sharing)
    ? sharing
    : sharing && typeof sharing === 'object'
      ? Object.keys(sharing).map((type) => ({ type, rent: sharing[type] }))
      : [];
  const order = TAXONOMY.options.pgSharing.map((s) => s.id);
  return list
    .map((s) => ({ type: s && s.type, rent: toNumber(s && s.rent) }))
    .filter((s) => order.includes(s.type) && s.rent !== null && s.rent > 0)
    .sort((a, b) => order.indexOf(a.type) - order.indexOf(b.type));
}

/** "2 BHK Apartment for Rent in HSR Layout": always passes validateTitle. */
function suggestTitle(values) {
  const v = values || {};
  const category = normalizeCategory(v.propertyCategory) || 'residential';
  const def = getPropertyTypeDef(category, v.propertyType);
  const kind = getKind(category, v.propertyType);
  const listing = normalizeListingType(v.listingType || v.category, category);
  const place = String(v.locality || v.city || '').trim();
  const where = place ? ` in ${place}` : '';
  const verb = listing === 'buy' ? 'Sale' : listing === 'lease' ? 'Lease' : 'Rent';
  const name = def ? def.shortLabel : 'Property';

  let title;
  if (kind === 'pg') {
    const who = v.pgFor ? `${getOptionLabel('pgFor', v.pgFor).replace(' (anyone)', '')} ` : '';
    title = who || where ? `${who}PG${where}` : 'PG Accommodation';
  } else if (kind === 'plot') {
    const area = toNumber(v.areaValue);
    const size = area ? `${formatIndianNumber(area)} ${getAreaUnit(v.areaUnit).label} ` : '';
    title = `${size}${name} for ${verb}${where}`;
  } else if (kind === 'home' && def && !def.noBhk && toNumber(v.rooms)) {
    const rooms = toNumber(v.rooms);
    title = `${rooms >= 5 ? '5+' : rooms} BHK ${name} for ${verb}${where}`;
  } else {
    title = `${name} for ${verb}${where}`;
  }
  title = title.replace(TITLE_FORBIDDEN_ALL, '').replace(/\s+/g, ' ').trim();
  return title.length > TAXONOMY.limits.titleMax ? title.slice(0, TAXONOMY.limits.titleMax).trim() : title;
}

function joinWithAnd(items) {
  const list = items.filter(Boolean);
  if (list.length <= 1) return list.join('');
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

function ordinal(n) {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] || 'th'}`;
}

/** A plain description built from the details, for "Write it for me". */
function suggestDescription(values) {
  const v = values || {};
  const category = normalizeCategory(v.propertyCategory) || 'residential';
  const def = getPropertyTypeDef(category, v.propertyType);
  const kind = getKind(category, v.propertyType);
  const listing = normalizeListingType(v.listingType || v.category, category);
  const place = [v.societyName, v.locality, v.city].map((s) => String(s || '').trim()).filter(Boolean).join(', ');
  const sentences = [];

  const config = kind === 'home' && def && !def.noBhk && toNumber(v.rooms)
    ? `${toNumber(v.rooms) >= 5 ? '5+' : toNumber(v.rooms)} BHK ` : '';
  const name = def ? (kind === 'commercial' ? def.label : def.shortLabel).toLowerCase() : 'property';
  const verb = listing === 'buy' ? 'for sale' : listing === 'lease' ? 'for lease' : 'for rent';
  if (kind === 'pg') {
    const who = v.pgFor ? `${getOptionLabel('pgFor', v.pgFor).replace(' (anyone)', '').toLowerCase()} ` : '';
    sentences.push(`Comfortable ${who}PG accommodation${place ? ` in ${place}` : ''}.`);
  } else {
    sentences.push(`${config ? config : ''}${name} ${verb}${place ? ` in ${place}` : ''}.`.replace(/^./, (c) => c.toUpperCase()));
  }

  const area = formatArea({ area: toSqft(v.areaValue, v.areaUnit), areaValue: v.areaValue, areaUnit: v.areaUnit });
  if (area) sentences.push(`Area: ${area}${v.areaType ? ` (${getOptionLabel('areaTypes', v.areaType).toLowerCase()})` : ''}.`);

  const facts = [];
  if (v.furnishing) facts.push(getOptionLabel('furnishing', v.furnishing).toLowerCase());
  const floor = toNumber(v.floorNumber);
  const floors = toNumber(v.totalFloors);
  if (floor !== null && floors) facts.push(floor === 0 ? `ground floor of ${floors}` : `${ordinal(floor)} floor of ${floors}`);
  else if (floors && def && def.standalone) facts.push(`${floors} floor${floors > 1 ? 's' : ''}`);
  const baths = toNumber(v.bathrooms);
  if (kind === 'home' && baths) facts.push(`${baths >= 4 ? '4+' : baths} bathroom${baths > 1 ? 's' : ''}`);
  const parking = (toNumber(v.coveredParking) || 0) + (toNumber(v.openParking) || 0);
  if (parking) facts.push(`${parking} parking spot${parking > 1 ? 's' : ''}`);
  if (v.facing) facts.push(`${getOptionLabel('facing', v.facing).toLowerCase()} facing`);
  if (facts.length) sentences.push(`Highlights: ${facts.join(', ')}.`);

  if (kind === 'pg') {
    const rents = getPgSharingRents(v);
    if (rents.length) {
      sentences.push(`Rooms: ${joinWithAnd(rents.map((r) => `${getOptionLabel('pgSharing', r.type).toLowerCase()} at ₹${formatIndianNumber(r.rent)}`))} per bed a month.`);
    }
    const schedule = normalizeMealSchedule(v.mealSchedule);
    const food = v.foodType ? ` (${getOptionLabel('foodType', v.foodType).toLowerCase()})` : '';
    if (schedule) {
      sentences.push(`Meals included${food}. ${describeMealSchedule(schedule)}.`);
    } else if (Array.isArray(v.meals) && v.meals.length) {
      sentences.push(`Meals included: ${joinWithAnd(v.meals.map((m) => getOptionLabel('meals', m).toLowerCase()))}${food}.`);
    }
    if (Array.isArray(v.pgServices) && v.pgServices.length) {
      sentences.push(`Services: ${joinWithAnd(v.pgServices.map((s) => getOptionLabel('pgServices', s)))}.`);
    }
  }

  if (listing === 'buy' && kind !== 'pg') {
    if (v.readyToMove === false || v.possession === 'under_construction') {
      sentences.push(`Under construction${v.possessionDate ? `, possession expected by ${v.possessionDate}` : ''}.`);
    } else if (kind !== 'plot') {
      sentences.push('Ready to move in.');
    }
    if (v.reraId) sentences.push(`RERA registration: ${String(v.reraId).trim()}.`);
  } else if (listing === 'rent' || listing === 'lease') {
    sentences.push(isAvailableNow(v.availableFrom) ? 'Available immediately.' : `Available from ${v.availableFrom}.`);
    const deposit = toNumber(v.securityDeposit);
    if (deposit) sentences.push(`Security deposit ₹${formatIndianNumber(deposit)}.`);
    if (Array.isArray(v.preferredTenants) && v.preferredTenants.length) {
      sentences.push(`Preferred tenants: ${joinWithAnd(v.preferredTenants.map((t) => getOptionLabel('preferredTenants', t).toLowerCase()))}.`);
    }
  }
  return sentences.join(' ').replace(/\s+/g, ' ').trim();
}

/**
 * Problems with a listing, keyed by field. Each surface shows the ones that
 * belong to the step the user is on. Photos are checked by the caller.
 */
function validateListingValues(values) {
  const v = values || {};
  const errors = {};
  const category = normalizeCategory(v.propertyCategory);
  const listing = normalizeListingType(v.listingType || v.category, category);
  const def = getPropertyTypeDef(category, v.propertyType);
  const kind = getKind(category, v.propertyType);

  if (!listing) errors.listingType = 'Choose whether you want to sell or rent out.';
  if (!category) errors.propertyCategory = 'Choose residential or commercial.';
  if (!def || (category && def.category !== category)) errors.propertyType = 'Choose a property type.';
  else if (listing && !def.listingTypes.includes(listing)) {
    errors.propertyType = `${def.label} can’t be listed to ${TAXONOMY.listingTypes.find((l) => l.id === listing).sellerLabel.toLowerCase()}.`;
  }

  if (!isPresent(v.locality)) errors.locality = 'Enter the locality or area.';
  if (!isPresent(v.city)) errors.city = 'Enter the city.';
  if (isPresent(v.pincode) && !/^\d{6}$/.test(String(v.pincode).trim())) errors.pincode = 'Pincode must be 6 digits.';

  if (kind === 'home' && hasBhk(category, v.propertyType) && !(toNumber(v.rooms) > 0)) errors.rooms = 'Choose the number of bedrooms.';
  if (kind !== 'pg' && !(toNumber(v.areaValue) > 0)) errors.areaValue = 'Enter the area.';
  if (kind === 'pg' && !isPresent(v.pgFor)) errors.pgFor = 'Choose who the PG is for.';

  // Only types that ask for a floor number (not houses, plots or PGs). A
  // leftover value from another type must never block the lister.
  if (listing && getApplicableFields(category, v.propertyType, listing).has('floorNumber')) {
    const floor = toNumber(v.floorNumber);
    const floors = toNumber(v.totalFloors);
    if (floor !== null && floors !== null && floor > floors) errors.floorNumber = 'Floor can’t be above the total floors.';
  }

  if (kind === 'pg') {
    if (getPgSharingRents(v).length === 0) errors.pgSharing = 'Add the rent for at least one room type.';
  } else if (!(toNumber(v.price) > 0)) {
    errors.price = 'Enter a price greater than 0.';
  }

  const titleError = validateTitle(v.title);
  if (titleError) errors.title = titleError;
  if (String(v.description || '').length > TAXONOMY.limits.descriptionMax) {
    errors.description = `Description must be under ${TAXONOMY.limits.descriptionMax} characters.`;
  }
  return errors;
}

// ─── Payload ────────────────────────────────────────────────────────────────

function cleanString(v) {
  return typeof v === 'string' ? v.trim() : v === undefined || v === null ? '' : String(v).trim();
}

/**
 * The Firestore fields for a listing, built the same way on every surface.
 * Callers add what is surface-specific: ids, owner fields, coordinates /
 * geohash / state, images, status and timestamps.
 *
 * Returns { fields, removedKeys }: `removedKeys` are optional fields that
 * don't apply (or were cleared): delete them on update, omit them on create.
 */
function buildListingFields(values) {
  const v = values || {};
  const category = normalizeCategory(v.propertyCategory) || 'residential';
  const listing = normalizeListingType(v.listingType || v.category, category) || 'buy';
  const propertyType = normalizePropertyType(v.propertyType) || '';
  const def = getPropertyTypeDef(category, propertyType);
  const kind = getKind(category, propertyType);
  const applicable = getApplicableFields(category, propertyType, listing);

  const pgRents = kind === 'pg' ? getPgSharingRents(v) : [];
  const mealSchedule = kind === 'pg' ? normalizeMealSchedule(v.mealSchedule) : null;
  const price = kind === 'pg'
    ? (pgRents.length ? Math.min(...pgRents.map((r) => r.rent)) : 0)
    : (toNumber(v.price) || 0);

  const areaUnit = getAreaUnit(v.areaUnit).id;
  const areaValue = toNumber(v.areaValue);
  const isSale = listing === 'buy';
  // Only buildings have a possession status; plots and PGs are always "ready".
  const tracksPossession = isSale && kind !== 'plot' && kind !== 'pg';
  const readyToMove = tracksPossession ? !(v.readyToMove === false || v.possession === 'under_construction') : true;
  const rooms = kind === 'home' && def && !def.noBhk ? Math.max(0, Math.round(toNumber(v.rooms) || 0)) : 0;
  const bathrooms = kind === 'home' ? Math.max(0, Math.round(toNumber(v.bathrooms) || 0)) : 0;

  const fields = {
    title: cleanString(v.title),
    description: cleanString(v.description),
    price,
    priceUnit: getPriceUnit(listing, kind),
    isNegotiable: !!v.isNegotiable,
    address: cleanString(v.address),
    locality: cleanString(v.locality),
    city: cleanString(v.city),
    pincode: cleanString(v.pincode),
    societyName: cleanString(v.societyName),
    googleMapsUrl: cleanString(v.googleMapsUrl),
    category: listing,
    propertyCategory: category,
    propertyType,
    rooms,
    bathrooms,
    // PGs are priced per bed and have no area input, so never carry one over.
    area: kind === 'pg' ? 0 : toSqft(areaValue, areaUnit),
    areaValue: kind !== 'pg' && areaValue && areaValue > 0 ? areaValue : 0,
    areaUnit,
    readyToMove,
    possessionDate: isSale && !readyToMove ? cleanString(v.possessionDate) : '',
    availableFrom: isSale ? '' : (cleanString(v.availableFrom) || 'Immediately'),
    facilities: Array.isArray(v.facilities) ? v.facilities.filter((f) => typeof f === 'string') : [],
    officeId: cleanString(v.officeId),
  };

  const removedKeys = [];
  for (const key of OPTIONAL_FIELDS) {
    if (!applicable.has(key)) { removedKeys.push(key); continue; }
    let value;
    if (key === 'pgSharing') value = pgRents.length ? pgRents : null;
    else if (key === 'possessionDate' || key === 'availableFrom') continue;
    else if (key === 'mealSchedule') value = mealSchedule;
    // With a per-day plan, `meals` is every meal served on any day.
    else if (key === 'meals' && mealSchedule) value = MEAL_IDS.filter((m) => WEEK_DAYS.some((d) => mealSchedule[d].includes(m)));
    else if (NUMERIC_FIELDS.includes(key)) value = toNumber(v[key]);
    else if (BOOLEAN_FIELDS.includes(key)) value = v[key] === true ? true : v[key] === false ? false : null;
    else if (ARRAY_FIELDS.includes(key)) value = Array.isArray(v[key]) ? v[key].filter(Boolean) : null;
    else value = isPresent(v[key]) ? cleanString(v[key]) : null;

    if (key === 'currentRent' && v.preLeased !== true) value = null;
    if (key === 'maintenance' && v.maintenanceIncluded === true) value = null;
    if (key === 'ageOfProperty' && !readyToMove) value = null;
    // Booleans: only `true` is stored for yes/no extras, so an untouched
    // switch doesn't put "No" on the listing (maintenance > 0 without
    // maintenanceIncluded already means "extra").
    if (BOOLEAN_FIELDS.includes(key) && value === false) value = null;

    if (value === null || value === undefined || (Array.isArray(value) && value.length === 0)) {
      removedKeys.push(key);
    } else {
      fields[key] = value;
    }
  }
  return { fields, removedKeys };
}

/**
 * Listing-form values from a stored document, for editing on any surface.
 * Normalises legacy values ('apartment', 'sell', commercial 'rent', string
 * prices) and never invents bedrooms/bathrooms for types that have none.
 */
function listingToValues(doc) {
  const d = doc || {};
  const propertyCategory = normalizeCategory(d.propertyCategory) || 'residential';
  const propertyType = normalizePropertyType(d.propertyType) || '';
  const def = getPropertyTypeDef(propertyCategory, propertyType);
  const kind = getKind(propertyCategory, propertyType);
  const listingType = normalizeListingType(d.category || d.listingType, propertyCategory) || '';
  const numStr = (x) => (toNumber(x) === null ? '' : String(toNumber(x)));

  const values = {
    title: d.title || '',
    description: d.description || '',
    listingType,
    propertyCategory,
    propertyType,
    price: kind === 'pg' ? '' : numStr(d.price),
    isNegotiable: !!d.isNegotiable,
    address: d.address || '',
    locality: d.locality || '',
    city: d.city || '',
    state: d.state || '',
    pincode: d.pincode || '',
    societyName: d.societyName || '',
    googleMapsUrl: d.googleMapsUrl || '',
    rooms: kind === 'home' && def && !def.noBhk && toNumber(d.rooms) > 0 ? toNumber(d.rooms) : null,
    bathrooms: kind === 'home' && toNumber(d.bathrooms) > 0 ? toNumber(d.bathrooms) : null,
    areaUnit: d.areaUnit ? getAreaUnit(d.areaUnit).id : 'sqft',
    areaValue: d.areaUnit && toNumber(d.areaValue) ? String(toNumber(d.areaValue)) : numStr(d.area && toNumber(d.area) > 0 ? d.area : ''),
    possession: listingType === 'buy' && d.readyToMove === false ? 'under_construction' : 'ready',
    readyToMove: d.readyToMove !== false,
    possessionDate: d.possessionDate || '',
    availableFrom: d.availableFrom || 'Immediately',
    facilities: Array.isArray(d.facilities) ? d.facilities.slice() : [],
    officeId: d.officeId || '',
    pgSharing: {},
  };

  for (const key of OPTIONAL_FIELDS) {
    if (key === 'pgSharing' || key === 'possessionDate' || key === 'availableFrom') continue;
    if (key === 'mealSchedule') {
      values.mealSchedule = normalizeMealSchedule(d.mealSchedule);
      continue;
    }
    const raw = d[key];
    if (raw === undefined || raw === null) {
      values[key] = ARRAY_FIELDS.includes(key) ? [] : BOOLEAN_FIELDS.includes(key) ? false : '';
    } else if (NUMERIC_FIELDS.includes(key)) {
      values[key] = numStr(raw);
    } else if (ARRAY_FIELDS.includes(key)) {
      values[key] = Array.isArray(raw) ? raw.slice() : [];
    } else {
      values[key] = raw;
    }
  }
  if (Array.isArray(d.pgSharing)) {
    d.pgSharing.forEach((s) => {
      if (s && s.type && toNumber(s.rent)) values.pgSharing[s.type] = String(toNumber(s.rent));
    });
  }
  return values;
}

// ─── Filters ────────────────────────────────────────────────────────────────

/**
 * Whether a listing matches browse / saved-search filters. Location and
 * distance are applied by the caller (the app queries by geohash; the alert
 * function measures distance itself).
 */
function matchesListingFilters(listing, filters, options) {
  if (!listing) return false;
  const f = filters || {};
  const now = options && options.now;
  const category = normalizeCategory(listing.propertyCategory) || 'residential';
  const listingType = normalizeListingType(listing.category || listing.listingType, category);
  const type = normalizePropertyType(listing.propertyType);
  const def = getPropertyTypeDef(category, type);
  const kind = getKind(category, type);

  const wantedCategory = f.propertyCategory && f.propertyCategory !== 'all' ? f.propertyCategory : null;
  if (wantedCategory && wantedCategory !== category) return false;

  if (f.listingType) {
    const wanted = TAXONOMY.listingTypeAliases[f.listingType] || f.listingType;
    // Rent across all categories includes commercial leases (Explore's Rent
    // tab under "All" shows both).
    const rentLike = wanted === 'rent' && !wantedCategory && listingType === 'lease';
    if (wanted !== listingType && !rentLike) return false;
  }

  if (Array.isArray(f.propertyTypes) && f.propertyTypes.length > 0) {
    const wantedTypes = f.propertyTypes.map(normalizePropertyType);
    if (!type || !wantedTypes.includes(type)) return false;
  }

  if (Array.isArray(f.rooms) && f.rooms.length > 0) {
    if (kind !== 'home') return false;
    const isStudio = !!(def && def.noBhk);
    const rooms = toNumber(listing.rooms) || 0;
    const ok = f.rooms.some((r) => (r === 0 ? isStudio : !isStudio && (r >= 4 ? rooms >= 4 : rooms === r)));
    if (!ok) return false;
  }

  const priceMin = toNumber(f.priceMin) || 0;
  const priceMax = toNumber(f.priceMax) || 0;
  if (priceMin > 0 || priceMax > 0) {
    const price = toNumber(listing.price);
    if (price === null) return false;
    if (priceMin > 0 && price < priceMin) return false;
    if (priceMax > 0 && price > priceMax) return false;
  }

  if (f.readyToMoveOnly) {
    if (listingType === 'buy') {
      if (listing.readyToMove === false) return false;
    } else if (!isAvailableNow(listing.availableFrom, now)) {
      return false;
    }
  }

  if (Array.isArray(f.furnishing) && f.furnishing.length > 0 && !f.furnishing.includes(listing.furnishing)) return false;

  if (f.postedBy === 'dealer' && listing.ownerRole !== 'dealer') return false;
  if (f.postedBy === 'owner' && listing.ownerRole === 'dealer') return false;

  if (Array.isArray(f.amenities) && f.amenities.length > 0) {
    const have = Array.isArray(listing.facilities) ? listing.facilities : [];
    if (!f.amenities.every((a) => have.includes(a))) return false;
  }

  if (Array.isArray(f.pgFor) && f.pgFor.length > 0) {
    if (kind !== 'pg') return false;
    if (listing.pgFor !== 'coliving' && !f.pgFor.includes(listing.pgFor)) return false;
  }

  if (Array.isArray(f.preferredTenants) && f.preferredTenants.length > 0) {
    const accepts = Array.isArray(listing.preferredTenants) ? listing.preferredTenants : [];
    if (accepts.length > 0 && !f.preferredTenants.some((t) => accepts.includes(t))) return false;
  }

  const areaMin = toNumber(f.areaMin) || 0;
  const areaMax = toNumber(f.areaMax) || 0;
  if (areaMin > 0 || areaMax > 0) {
    const area = toNumber(listing.area) || 0;
    if (areaMin > 0 && area < areaMin) return false;
    if (areaMax > 0 && area > areaMax) return false;
  }

  const q = String(f.searchQuery || '').trim().toLowerCase();
  if (q) {
    const config = getConfigLabel(listing).toLowerCase();
    const haystack = [
      listing.title, listing.locality, listing.city, listing.address, listing.societyName,
      getTypeLabel(type, category), getTypeLabel(type, category, true), config, config.replace(/\s+/g, ''),
    ].map((s) => String(s || '').toLowerCase());
    if (!haystack.some((s) => s.includes(q))) return false;
  }
  return true;
}

module.exports = {
  TAXONOMY,
  LISTING_TYPE_IDS,
  CATEGORY_IDS,
  PROPERTY_TYPE_IDS,
  CORE_FIELDS,
  OPTIONAL_FIELDS,
  toNumber,
  formatIndianNumber,
  formatPriceShort,
  normalizeCategory,
  normalizePropertyType,
  normalizeListingType,
  getPropertyTypeDef,
  getKind,
  getPropertyTypes,
  getListingTypesFor,
  isValidCombination,
  getTypeLabel,
  getListingTypeDef,
  getListingBadge,
  getOptionLabel,
  getFurnishingOptions,
  getPriceUnit,
  getPriceBrackets,
  getApplicableFields,
  hasBhk,
  getAreaUnit,
  toSqft,
  formatArea,
  getConfigLabel,
  isBrowseVisible,
  isAvailableNow,
  validateTitle,
  getPgSharingRents,
  normalizeMealSchedule,
  describeMealSchedule,
  suggestTitle,
  suggestDescription,
  validateListingValues,
  buildListingFields,
  listingToValues,
  matchesListingFilters,
};
